// src/components/auth/MinimalRegister/MinimalRegister.jsx

import React, { useContext, useState } from "react";
import { Form, Button, Spinner } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import axios from "axios";
import Swal from "sweetalert2";
import "../../../../assets/css/customer/auth/AuthForms.css";
import MyContext from "../../../../context/MyContext";

// ── Pakistan Cities — alphabetically sorted ──
const PAKISTAN_CITIES = [
  "Abbottabad",
  "Attock",
  "Bahawalpur",
  "Bahawalnagar",
  "Burewala",
  "Chakwal",
  "Chiniot",
  "Chishtian",
  "Daska",
  "Dera Ghazi Khan",
  "Dera Ismail Khan",
  "Faisalabad",
  "Ghotki",
  "Gujranwala",
  "Gujrat",
  "Hafizabad",
  "Haripur",
  "Hyderabad",
  "Islamabad",
  "Jacobabad",
  "Jhelum",
  "Kamoke",
  "Karachi",
  "Kasur",
  "Khanewal",
  "Khushab",
  "Khuzdar",
  "Lahore",
  "Larkana",
  "Layyah",
  "Lodhran",
  "Mailsi",
  "Mansehra",
  "Mardan",
  "Mianwali",
  "Mirpur Khas",
  "Multan",
  "Muridke",
  "Muzaffarabad",
  "Muzaffargarh",
  "Narowal",
  "Nawabshah",
  "Nowshera",
  "Okara",
  "Pakpattan",
  "Peshawar",
  "Quetta",
  "Rahim Yar Khan",
  "Rawalpindi",
  "Sadiqabad",
  "Sahiwal",
  "Sargodha",
  "Sheikhupura",
  "Sialkot",
  "Sukkur",
  "Swabi",
  "Swat",
  "Toba Tek Singh",
  "Vehari",
  "Wah Cantonment",
  "Wazirabad",
].sort();

const Register = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [city, setCity] = useState("");
  const [nameError, setNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [cityError, setCityError] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const { baseUrl, regExpChecker, setLoggedInCustomer } = useContext(MyContext);
  const navigate = useNavigate();

  // NEW: register ab 2-step ho gaya hai — pehle OTP jata hai
  // (sendRegisterOtp), phir OTP verify hone ke baad account banta
  // hai (verifyRegisterOtp). step 1 = form, step 2 = OTP.
  const [step, setStep] = useState(1);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [resending, setResending] = useState(false);

  const sendRegisterOtp = ({ isResend = false } = {}) => {
    if (!isResend) {
      const nameCheck = regExpChecker("username", name);
      const emailCheck = regExpChecker("email", email);
      const passwordCheck = regExpChecker("password", password);
      const cityCheck = city ? "" : "City is required";

      setNameError(nameCheck);
      setEmailError(emailCheck);
      setPasswordError(passwordCheck);
      setCityError(cityCheck);

      if (name === "") {
        setNameError("Full name is required");
      }
      if (email === "") {
        setEmailError("Email is required");
      }
      if (password === "") {
        setPasswordError("Password is required");
      }

      if (
        name === "" ||
        email === "" ||
        password === "" ||
        city === "" ||
        nameCheck ||
        emailCheck ||
        passwordCheck ||
        cityCheck
      ) {
        return;
      }
    }

    setErrorMessage("");
    isResend ? setResending(true) : setLoading(true);

    const formData = new FormData();
    formData.append("name", name);
    formData.append("email", email);
    formData.append("password", password);
    formData.append("city", city);

    axios
      .post(`${baseUrl}/customer/auth/sendRegisterOtp`, formData)
      .then((response) => {
        isResend ? setResending(false) : setLoading(false);
        const data = response.data;
        if (data.success) {
          Swal.fire(
            "Sent!",
            isResend
              ? "A new verification code has been sent to your email."
              : "A verification code has been sent to your email.",
            "success"
          );
          setStep(2);
        } else {
          setErrorMessage(data.message || "Something went wrong. Please try again.");
        }
      })
      .catch((error) => {
        console.error("Send OTP Error:", error);
        isResend ? setResending(false) : setLoading(false);
        setErrorMessage("Something went wrong. Please try again.");
      });
  };

  const resendOtp = () => {
    sendRegisterOtp({ isResend: true });
  };

  const verifyRegisterOtp = () => {
    if (!otp) {
      setOtpError("Please enter the code sent to your email");
      return;
    }

    setOtpError("");
    setErrorMessage("");
    setLoading(true);

    const formData = new FormData();
    formData.append("email", email);
    formData.append("otp", otp);

    axios
      .post(`${baseUrl}/customer/auth/verifyRegisterOtp`, formData)
      .then((response) => {
        const data = response.data;

        if (data.success) {
          if (data.token) {
            localStorage.setItem("authToken", data.token);
          }

          localStorage.setItem("userEmail", email);
          localStorage.setItem("userPassword", password);

          if (data.customer) {
            localStorage.setItem(
              "loggedInCustomer",
              JSON.stringify(data.customer),
            );
            setLoggedInCustomer(data.customer);
          }

          setLoading(false);
          setTimeout(() => {
            navigate("/customer/login");
          }, 1000);
        } else {
          setLoading(false);
          setOtpError(data.message || "Invalid OTP. Please try again.");
        }
      })
      .catch((error) => {
        console.error("Verify OTP Error:", error);
        setLoading(false);
        setOtpError("Something went wrong. Please try again.");
      });
  };

  const changeName = (event) => {
    setName(event.target.value);
    setNameError(regExpChecker("username", event.target.value));
  };

  const changeEmail = (event) => {
    setEmail(event.target.value);
    setEmailError(regExpChecker("email", event.target.value));
  };

  const changePassword = (event) => {
    setPassword(event.target.value);
    setPasswordError(regExpChecker("password", event.target.value));
  };

  const changeCity = (event) => {
    const value = event.target.value;
    setCity(value);
    setCityError(value ? "" : "City is required");
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const submitRegisterForm = (event) => {
    event.preventDefault();
    setErrorMessage("");

    if (step === 1) {
      sendRegisterOtp();
    } else {
      verifyRegisterOtp();
    }
  };

  return (
    <div className="minimal-auth-wrapper">
      {loading ? (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(255,255,255,0.9)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Spinner
            animation="border"
            role="status"
            style={{ width: "4rem", height: "4rem" }}
          />
          <p style={{ marginTop: "15px", fontSize: "18px" }}>
            {step === 1 ? "Sending verification code..." : "Verifying..."}
          </p>
        </div>
      ) : (
        <div className="minimal-auth-container">
          <div className="minimal-auth-card">
            <div className="auth-header">
              <h1 className="auth-title">Create your account</h1>
              <p className="auth-subtitle">
                {step === 1
                  ? "We'll email you a code to verify it's really you."
                  : `We've sent a 6-digit code to ${email}.`}
              </p>
              <p className="auth-subtitle">
                Already have an account?
                <Link to="/customer/login" className="auth-link">
                  Log in
                </Link>
              </p>
            </div>

            <Form onSubmit={submitRegisterForm} className="minimal-auth-form">
              {step === 1 ? (
                <>
                  <div className="authform-group-minimal">
                    <Form.Control
                      type="text"
                      placeholder="Full name"
                      value={name}
                      onChange={changeName}
                      className="minimal-input"
                    />
                    {nameError && (
                      <p style={{ color: "red", fontSize: "13px" }}>{nameError}</p>
                    )}
                  </div>

                  <div className="authform-group-minimal">
                    <Form.Control
                      type="email"
                      placeholder="Email"
                      value={email}
                      onChange={changeEmail}
                      className="minimal-input"
                    />
                    {emailError && (
                      <p style={{ color: "red", fontSize: "13px" }}>{emailError}</p>
                    )}
                  </div>

                  <div className="authform-group-minimal">
                    <Form.Select
                      value={city}
                      onChange={changeCity}
                      className="minimal-input"
                    >
                      <option value="">Select your city</option>
                      {PAKISTAN_CITIES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </Form.Select>
                    {cityError && (
                      <p style={{ color: "red", fontSize: "13px" }}>{cityError}</p>
                    )}
                  </div>

                  <div className="authform-group-minimal">
                    <div className="password-input-wrapper">
                      <Form.Control
                        type={showPassword ? "text" : "password"}
                        placeholder="Password"
                        value={password}
                        onChange={changePassword}
                        className="minimal-input"
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={togglePasswordVisibility}
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                    {passwordError && (
                      <p style={{ color: "red", fontSize: "13px" }}>
                        {passwordError}
                      </p>
                    )}
                  </div>

                  {errorMessage && <p style={{ color: "red" }}>{errorMessage}</p>}

                  <Button type="submit" className="minimal-submit-btn">
                    Send Verification Code
                  </Button>
                </>
              ) : (
                <>
                  <div className="authform-group-minimal">
                    <Form.Control
                      type="text"
                      placeholder="6-digit code"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      className="minimal-input"
                    />
                    {otpError && (
                      <p style={{ color: "red", fontSize: "13px" }}>{otpError}</p>
                    )}
                  </div>

                  {errorMessage && <p style={{ color: "red" }}>{errorMessage}</p>}

                  <Button type="submit" className="minimal-submit-btn">
                    Verify &amp; Create Account
                  </Button>

                  <p style={{ textAlign: "center", marginTop: "0.9rem", fontSize: "0.875rem", color: "#555" }}>
                    Didn't receive the code?{" "}
                    <span
                      onClick={() => !resending && resendOtp()}
                      style={{
                        color: "#097d43",
                        fontWeight: 600,
                        cursor: resending ? "default" : "pointer",
                        textDecoration: "underline",
                      }}
                    >
                      {resending ? "Resending..." : "Resend"}
                    </span>
                  </p>

                  <button
                    type="button"
                    onClick={() => {
                      setStep(1);
                      setOtp("");
                      setOtpError("");
                      setErrorMessage("");
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#097d43",
                      marginTop: "0.25rem",
                      fontSize: "0.875rem",
                    }}
                  >
                    ← Edit details
                  </button>
                </>
              )}
            </Form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Register;
